import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { IMAGE_SIZE_CODE } from '../../../../../../../../../constants/image-size-codes';
import { getThumbBySize } from '../../../../../../../../../util/photo';
import { getAsset } from '../../../../../../../../../util/assets';
import { getHierarchyItemSubtitle } from './helpers';
import { getHierarchyItemCoverPhoto } from '../Hierarchy/helpers';
import WatermarkOverlay from '../../../../../../../../../components/global/WatermarkOverlay';
import { getFaceboxes } from '../../../../../Matches/components/MatchedPhoto/helpers';
import PhotoObfuscated from '../../../../../../../../../components/global/obfuscation/PhotoObfuscated';
import useComponentBounds from '../../../../../../../../../hooks/useComponentBounds';

const propTypes = {
  hierarchyItem: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  icon: PropTypes.node,
  isActive: PropTypes.bool,
  faceBlock: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
  className: PropTypes.string,
  thumbnailActions: PropTypes.node,
  subtitle: PropTypes.string,
};

const HierarchyItemCard = ({
  hierarchyItem,
  icon,
  linkTo,
  isActive,
  faceBlock,
  watermarkSettings,
  className,
  thumbnailActions,
  subtitle: subtitleProp,
}) => {
  const thumbnailContainerRef = useRef(null);
  const { bounds } = useComponentBounds(thumbnailContainerRef);

  const coverPhoto = getHierarchyItemCoverPhoto(hierarchyItem);

  const thumbnailUrl = getThumbBySize(
    coverPhoto?.thumbnail_urls ?? [],
    IMAGE_SIZE_CODE.MEDIUM2X,
    // Important for when using local cache
    true,
  )?.url;

  const title = hierarchyItem?.name ?? hierarchyItem?.album?.name ?? '';

  const classes = classNames({
    'hierarchy-item-card': true,
    'has-thumbnail': !!thumbnailUrl,
    'is-active': isActive,
    [className]: !!className,
  });

  // eslint-disable-next-line react/prop-types
  const TemplateLink = ({ children }) => (
    <Link to={linkTo} className="hierarchy-item-card-link">
      {children}
    </Link>
  );

  const thumbnail = (() => {
    if (!thumbnailUrl) {
      return (
        <TemplateLink>
          <div className="hierarchy-item-card-cover-null-state">
            <img
              alt="Waldo logo"
              className="m-auto d-flex grayscale-faded"
              width="60px"
              src={getAsset('/waldo-icon.svg')}
            />
            <p className="text-extra-bold text-center text-dark pt-4">
              See photos
            </p>
          </div>
        </TemplateLink>
      );
    }

    return (
      <TemplateLink>
        {faceBlock ? (
          <div className="hierarchy-item-card-photo-obfuscated-container">
            <PhotoObfuscated
              id={`hierarchy-item-cover-${hierarchyItem.id}`}
              className="hierarchy-item-card-cover-obfuscated"
              thumbs={coverPhoto.thumbnail_urls}
              blockerMinSizeThreshold={0.005}
              blockerMinSize={0.3}
              blockerMaxSize={0.5}
              maxThumbnailSize={IMAGE_SIZE_CODE.LARGE}
              faceboxes={getFaceboxes(null, true)}
              enableFaceblock={
                !watermarkSettings || !bounds?.width || !bounds?.height
              }
            />
            {watermarkSettings && bounds?.width > 0 && bounds?.height > 0 && (
              <div className="watermark-container">
                <WatermarkOverlay
                  text={watermarkSettings?.wallpaperCustomText}
                  fontSize={watermarkSettings?.wallpaperFontSize}
                  wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
                  imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
                  width={bounds.width}
                  height={bounds.height}
                />
              </div>
            )}
          </div>
        ) : (
          <img
            src={thumbnailUrl}
            alt={title}
            className="hierarchy-item-card-cover-img"
          />
        )}
      </TemplateLink>
    );
  })();

  const subtitle = getHierarchyItemSubtitle(hierarchyItem, subtitleProp);

  return (
    <div className={classes}>
      <figure ref={thumbnailContainerRef} className="hierarchy-item-card-cover">
        {thumbnail}
        {thumbnailActions && (
          <div className="hierarchy-item-card-thumbnail-actions">
            {thumbnailActions}
          </div>
        )}
      </figure>

      <div className="hierarchy-item-card-content">
        {icon && <span className="hierarchy-item-card-cover-icon">{icon}</span>}
        <TemplateLink>
          <p className="hierarchy-item-card-title">{title}</p>

          {subtitle && (
            <div className="hierarchy-item-card-content-footer">
              {subtitle && (
                <span className="hierarchy-item-card-subtitle">{subtitle}</span>
              )}
            </div>
          )}
        </TemplateLink>
      </div>
    </div>
  );
};

HierarchyItemCard.propTypes = propTypes;

export default HierarchyItemCard;
